import { Suspense, lazy, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { resolvePose, type EconomyNaming, type PublicWheelSegment } from '@shared';
import { useGame } from '../../game/store';
import { apiUrl } from '../../lib/api';
import {
  creditWord,
  formatCountdown,
  formatCreditAmount,
  formatCredits,
  formatRelative,
  imageUrl,
} from '../../lib/format';
import { useSession } from '../../state/session';
import { RewardBadge } from '../../dashboard/components/RewardBadge';
import { Icon, type IconName } from '../ui/Icon';
import { creditIconFor } from '../ui/currency';
import { Panel } from '../ui/Panel';
import { Mascot } from '../mascot/Mascot';
import { mascotStateForOutcome, type MascotState } from '../mascot/states';
import type { RevealedOutcome } from '../wheel/types';
import { ScratchSurface, type ScratchSurfaceHandle } from './ScratchSurface';
import styles from './ScratchCard.module.css';

/** Same split as the wheel: the payoff dialog is heavy and only matters at the reveal. */
const loadRewardDialog = () => import('../wheel/RewardDialog');
const RewardDialog = lazy(async () => ({ default: (await loadRewardDialog()).RewardDialog }));

type Phase = 'idle' | 'scratching' | 'revealed';

function iconFor(outcome: RevealedOutcome, creditIcon: IconName): IconName {
  switch (outcome.reward) {
    case 'credits':
      return creditIcon;
    case 'xp':
      return 'bolt';
    case 'coupon':
      return 'ticket';
    case 'role':
      return 'shield';
    case 'custom':
      return 'gift';
    case 'respin':
      return 'refresh';
    default:
      return 'ban';
  }
}

function headline(outcome: RevealedOutcome, naming: EconomyNaming): string {
  if (outcome.reward === 'nothing') return 'Not this time';
  if (outcome.reward === 'credits')
    return `+${formatCredits(outcome.value)} ${creditWord(naming, outcome.value)}`;
  if (outcome.reward === 'xp') return `+${formatCredits(outcome.value)} ${naming.xpName}`;
  if (outcome.reward === 'coupon') return `${outcome.value}% coupon`;
  return outcome.name;
}

/**
 * The scratch card — a card is bought, then rubbed to reveal what it holds.
 *
 * It is the wheel's third draw wearing a different reveal, so every rule that
 * matters is the wheel's rule: the **draw happens on the server** at purchase, the
 * resulting balance is held back until the reveal (`spin` parks it, `commitSpin`
 * publishes it — leaving mid-card still pays out), and a second tap during the
 * round trip is ignored. This component only owns *when the player gets to see*.
 *
 * What a card costs is shown in credits, like a wheel spin, and never in money.
 * A credit prize is a `bonus` on the server: spendable, never a rank.
 */
export function ScratchCard() {
  const { wheel, wheelStatus, balance, spin, commitSpin, spins, naming } = useGame();
  const { theme, mascot } = useSession();
  const creditIcon = creditIconFor(theme);

  const config = wheel('scratch');
  const status = wheelStatus('scratch');

  const surface = useRef<ScratchSurfaceHandle>(null);
  const sending = useRef(false);

  const [phase, setPhase] = useState<Phase>('idle');
  const [outcome, setOutcome] = useState<RevealedOutcome | null>(null);
  const [dialog, setDialog] = useState<RevealedOutcome | null>(null);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [liveMessage, setLiveMessage] = useState('');

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      clearInterval(timer);
      // Leaving mid-card still pays out: the card was bought, and the server has
      // already drawn it.
      commitSpin();
    };
  }, [commitSpin]);

  const cover = resolvePose(mascot, 'card');
  const coverSrc = cover
    ? cover.src.startsWith('/mascot/')
      ? apiUrl(cover.src)
      : imageUrl(cover.src)
    : null;

  const mySpins = useMemo(() => spins.filter((s) => s.wheelId === 'scratch'), [spins]);
  const remaining = status.nextAt ? new Date(status.nextAt).getTime() - now : 0;
  const canBuy = status.canSpin && phase !== 'scratching' && !sending.current;

  async function buy() {
    // Same in-flight guard as the wheel: nothing in the store moves until the
    // server answers, so `canBuy` alone cannot stop a second tap.
    if (!status.canSpin || phase === 'scratching' || sending.current) return;
    sending.current = true;
    setError(null);
    void loadRewardDialog();

    const result = await spin('scratch');
    sending.current = false;

    if (!result.ok) {
      setError(
        result.reason === 'insufficient'
          ? `Not enough ${creditWord(naming)} for a card.`
          : result.reason === 'cooldown'
            ? 'You have to wait before the next card.'
            : result.reason === 'disabled'
              ? 'The scratch card is turned off right now.'
              : 'The card is not ready right now.',
      );
      return;
    }

    setOutcome(result);
    setProgress(0);
    setPhase('scratching');
    setLiveMessage('Your card is ready — scratch the foil to reveal your prize.');
  }

  function revealed() {
    if (!outcome) return;
    // Publish the balance and show the prize on the same paint.
    commitSpin();
    setPhase('revealed');
    setDialog(outcome);
    setLiveMessage(
      outcome.reward === 'nothing' ? 'No prize this time.' : `You won ${outcome.name}.`,
    );
  }

  const reaction: MascotState =
    phase === 'scratching'
      ? 'spinning'
      : phase === 'revealed' && outcome
        ? mascotStateForOutcome(outcome.reward, outcome.tier)
        : 'idle';

  const ctaLabel =
    phase === 'revealed'
      ? status.cost === 0
        ? 'Another card — free'
        : `Another card · ${formatCreditAmount(naming, status.cost)}`
      : status.cost === 0
        ? 'Get a card — free'
        : `Get a card · ${formatCreditAmount(naming, status.cost)}`;

  return (
    <>
      <div className={styles.layout}>
        <section className={styles.stageCard}>
          <header className={styles.header}>
            <span className={styles.balanceChip}>
              <Icon name={creditIcon} size={14} />
              {formatCredits(balance)}
            </span>
            <span className={styles.price}>
              {status.cost === 0 ? 'Free card' : `${formatCredits(status.cost)} / card`}
            </span>
          </header>

          <div
            className={styles.card}
            style={{ '--prize': outcome?.color ?? 'var(--brand-500)' } as CSSProperties}
          >
            {/* The prize sits under the foil, already decided by the server. */}
            <div className={styles.prize} aria-hidden={phase === 'scratching'}>
              {outcome ? (
                <>
                  <span className={styles.prizeIcon}>
                    <Icon name={iconFor(outcome, creditIcon)} size={36} />
                  </span>
                  <strong className={styles.prizeName}>{headline(outcome, naming)}</strong>
                  <span className={styles.prizeNote}>{outcome.name}</span>
                </>
              ) : (
                <>
                  <span className={styles.prizeIcon}>
                    <Icon name="gift" size={36} />
                  </span>
                  <strong className={styles.prizeName}>Your prize</strong>
                  <span className={styles.prizeNote}>Get a card to play</span>
                </>
              )}
            </div>

            <div className={`${styles.foilWrap} ${phase === 'revealed' ? styles.foilGone : ''}`}>
              <ScratchSurface
                ref={surface}
                coverSrc={coverSrc}
                active={phase === 'scratching'}
                onReveal={revealed}
                onProgress={setProgress}
              />
            </div>
          </div>

          <div className={styles.controls}>
            {phase === 'scratching' ? (
              <>
                <p className={styles.tagline}>
                  {progress < 0.15 ? 'Rub the foil to reveal your prize' : 'Keep going…'}
                </p>
                <button
                  type="button"
                  className={styles.secondary}
                  onClick={() => surface.current?.clearAll()}
                >
                  Reveal now
                </button>
              </>
            ) : status.onCooldown ? (
              <div className={styles.cooldown}>
                <span>Next card in</span>
                <strong>{formatCountdown(remaining)}</strong>
              </div>
            ) : (
              <>
                <p className={styles.tagline}>
                  {config.description || 'Scratch to reveal a prize.'}
                </p>
                <button type="button" className={styles.cta} onClick={buy} disabled={!canBuy}>
                  <Icon name="ticket" size={18} />
                  <span>{ctaLabel}</span>
                </button>
              </>
            )}

            {!config.enabled ? (
              <p className={styles.hint}>
                The scratch card is turned off right now. Check back soon.
              </p>
            ) : !status.affordable && phase !== 'scratching' ? (
              <p className={styles.hintWarn}>
                You need {formatCreditAmount(naming, status.cost - balance)} more for a card.
              </p>
            ) : error ? (
              <p className={styles.hintWarn}>{error}</p>
            ) : null}
          </div>

          <span className={styles.srOnly} role="status" aria-live="polite">
            {liveMessage}
          </span>
        </section>

        <aside className={styles.side}>
          <div className={styles.mascotCard}>
            <Mascot state={reaction} size={200} />
            <div className={styles.mascotWords}>
              <strong>
                {phase === 'scratching'
                  ? 'Scratch it!'
                  : phase === 'revealed'
                    ? 'There it is'
                    : 'Feeling lucky?'}
              </strong>
              <span>{config.name || 'Scratch Card'}</span>
            </div>
          </div>

          <Panel title="Prizes" subtitle={`${config.segments.length} on this card`}>
            <ul className={styles.prizes}>
              {config.segments.map((segment: PublicWheelSegment) => (
                <li key={segment.id} className={styles.prizeRow}>
                  <span className={styles.prizeDot} style={{ background: segment.color }} />
                  <span className={styles.prizeRowName}>{segment.name}</span>
                </li>
              ))}
            </ul>
          </Panel>

          <Panel title="Your cards" subtitle={`${mySpins.length} so far`}>
            {mySpins.length === 0 ? (
              <p className={styles.empty}>No cards yet.</p>
            ) : (
              <div className={styles.history}>
                {mySpins.slice(0, 6).map((entry) => (
                  <div key={entry.id} className={styles.historyItem}>
                    <span className={styles.historyBody}>
                      <span className={styles.historyLabel}>{entry.name}</span>
                      <span className={styles.historyMeta}>{formatRelative(entry.at)}</span>
                    </span>
                    <RewardBadge reward={entry.reward} naming={naming} />
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </aside>
      </div>

      <Suspense fallback={null}>
        <RewardDialog
          outcome={dialog}
          naming={naming}
          onClose={() => setDialog(null)}
          reducedMotion={false}
        />
      </Suspense>
    </>
  );
}
